<template>
  <div :class="searchAreaClass">
    <h4 class="d-flex justify-content-between mt-0">
      <template v-if="!formHidden">
        {{ i18n.global.t('pages.common.filter.title') }}
      </template>
      <svg
        width="24px"
        height="24px"
        viewBox="0 0 24 24"
        fit=""
        preserveAspectRatio="xMidYMid meet"
        focusable="false"
        sandboxuid="0"
        @click="toggleSidebar"
      >
        <template v-if="formHidden">
          <path
            d="M7.615 11.795l4.59-4.59-1.41-1.41-6 6 6 6 1.41-1.41-4.59-4.59zm9.59 6v-12h-2v12h2z"
            fill-rule="evenodd"
            sandboxuid="0"
          ></path>
        </template>
        <template v-if="!formHidden">
          <path
            d="M6 7.41L10.438 12 6 16.59 7.363 18l5.802-6-5.802-6L6 7.41zM16.066 6H18v12h-1.934V6z"
            sandboxuid="0"
          />
        </template>
      </svg>
    </h4>
    <div class="strpied-tabled-with-hover" :class="{ 'd-none': formHidden }">
      <div class="d-grid gap-2">
        <MultipleOperationOfficeAutoComplete
          v-model="operation_office_company"
        />
        <MultipleDeviceTypeAutoComplete
          v-model="selectedDeviceTypes"
        />
        <DeviceIdAutoComplete
          v-model="device"
          :operationOffices="operation_office_company"
        />
        <VehicleAutoComplete
          v-model="vehicle"
          :operationOffices="operation_office_company"
          :requestParams="{ type: 'filter' }"
        />
        <FirmwareVersionAutoComplete
          v-model="firmware"
        />
        <ValidationErrors :messages="errors['activationDateRangeEnd'] ?? []">
          <div class="my-2">
            <span class="small">{{ i18n.global.t('pages.deviceFirmware.applyDate') }}</span>
            <div style="font-size: 12px" class="w-100 d-flex justify-space-between m-1">
              <RangeDatePicker
                v-model="form.activationDateRange"
                :maxRange="deviceFirmwareDefaultDaysRange"
                :format="'yyyy/MM/dd'"
                :isError="errors?.activationDateRangeEnd?.length > 0"
              />
            </div>
          </div>
        </ValidationErrors>
      </div>
      <div class="d-flex flex-column">
        <hr />
        <div class="d-flex search-buttons">
          <v-btn
            class="ma-2 exec-btn"
            @click="handleClickSearch"
            :style="{
              backgroundColor: isDeviceListLoading ? '#B3B8BB' : '',
            }"
          >
            {{ filterBtnText }}
          </v-btn>
          <v-btn class="ma-2 reset-btn" @click="clearForm">
            {{ i18n.global.t('pages.common.button.reset') }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { getDateRangeDays } from '@/services/functions'
import { DEVICE_FIRMWARE_DEFAULT_DAYS_RANGE } from "@/services/consts";
import FirmwareVersionAutoComplete from '@/components/AutoCompletes/FirmwareVersionAutoComplete.vue';
import MultipleDeviceTypeAutoComplete from '@/components/AutoCompletes/MultipleDeviceTypeAutoComplete.vue';
import MultipleOperationOfficeAutoComplete from '@/components/AutoCompletes/MultipleOperationOfficeAutoComplete.vue';
import DeviceIdAutoComplete from '@/components/AutoCompletes/DeviceIdAutoComplete.vue';
import VehicleAutoComplete from '@/components/AutoCompletes/VehicleAutoComplete.vue';
import getOperationOffice from '@/types/responses/getOperationOffice';
import FirmwareVersionsType from '@/types/FirmwareVersionsType';
import getDeviceResponse from '@/types/responses/getDevice';
import getVehicleResponse from '@/types/responses/getVehicle';
import deviceType from '@/types/DeviceType';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import RangeDatePicker from '@/components/Common/RangeDatePicker.vue';
import i18n from '@/lang/i18n';

const currentDate = new Date()
const defaultStartOfRange = new Date(currentDate)
defaultStartOfRange.setDate(defaultStartOfRange.getDate() - DEVICE_FIRMWARE_DEFAULT_DAYS_RANGE)

export default defineComponent({
  setup() {
    return {
      i18n,
    };
  },
  data() {
    return {
      form: {
        version: undefined as undefined | string,
        device_id: undefined as undefined | number,
        ar_device_type_ids: [] as [] | number[],
        device_operation_office_companies: undefined as undefined | number[],
        firmware_id: undefined as undefined | number,
        vehicle_id: undefined as undefined | number,
        activationDateRange: [
          defaultStartOfRange,
          currentDate
        ] as Date[],
      },
      operation_office_company: [] as Array<getOperationOffice>,
      device: undefined as getDeviceResponse | undefined,
      vehicle: undefined as getVehicleResponse | undefined,
      firmware: undefined as undefined | FirmwareVersionsType,
      formHidden: false as boolean,
      selectedDeviceTypes: [] as Array<deviceType>,
      errors: {} as {[key: string]: string[]},
    };
  },
  props: {
    isDeviceListLoading: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
  },
  name: 'DeviceListSearchForm',
  computed: {
    searchAreaClass() {
      return this.formHidden ? 'search-area-hide p-4' : 'search-area p-4';
    },
    filterBtnText(): string {
      return this.isDeviceListLoading ?
        i18n.global.t('pages.common.button.loading') :
        i18n.global.t('pages.common.button.exec');
    },
    deviceFirmwareDefaultDaysRange(): number {
      return DEVICE_FIRMWARE_DEFAULT_DAYS_RANGE;
    },
  },
  emits: ['form-change', 'search'],
  watch: {
    form: {
      handler() {
        this.$emit('form-change', this.form);
      },
      deep: true,
    },
    operation_office_company: {
      handler() {
        if (this.operation_office_company) {
          this.form.device_operation_office_companies =
            this.operation_office_company.map((company) => company.id);
        } else {
          this.form.device_operation_office_companies = undefined;
        }
      },
      deep: true,
    },
    device: {
      handler() {
        if (this.device) {
          this.form.device_id = this.device.id;
        } else {
          this.form.device_id = undefined;
        }
      },
      deep: true,
    },
    vehicle: {
      handler() {
        if (this.vehicle) {
          this.form.vehicle_id = this.vehicle.id;
        } else {
          this.form.vehicle_id = undefined;
        }
      },
      deep: true,
    },
    firmware: {
      handler() {
        this.form.firmware_id = this.firmware?.id;
      },
      deep: true,
    },
    selectedDeviceTypes: {
      handler() {
        this.form.ar_device_type_ids = this.selectedDeviceTypes.map(
          (deviceType) => deviceType.id
        );
      },
      deep: true,
    },
  },
  methods: {
    validateRange(): boolean {
      this.errors['activationDateRangeEnd'] = []
      if (!this.form.activationDateRange[0] || !this.form.activationDateRange[1]) {
        this.errors['activationDateRangeEnd'] = [i18n.global.t('pages.common.filter.errorMssages.pleaseSelectCorrectRange')]
        return false
      }
      const start = new Date(this.form.activationDateRange[0])
      const end = new Date(this.form.activationDateRange[1])
      if (start.getTime() > end.getTime()) {
        this.errors['activationDateRangeEnd'] = [i18n.global.t('pages.common.filter.errorMssages.pleaseSelectCorrectRange')]
        return false
      }
      if (getDateRangeDays(start, end) > DEVICE_FIRMWARE_DEFAULT_DAYS_RANGE) {
        this.errors['activationDateRangeEnd'] = [`${i18n.global.t('pages.common.filter.errorMssages.range')}${DEVICE_FIRMWARE_DEFAULT_DAYS_RANGE}${i18n.global.t('pages.common.filter.errorMssages.pleaseSelectWithinDays')}`]
        return false
      }
      return true
    },
    handleClickSearch(): void {
      if (this.isDeviceListLoading) {
        return;
      }
      if (!this.validateRange()) {
        return;
      }
      this.$emit('search', this.form);
    },
    clearForm(): void {
      this.operation_office_company = [];
      this.firmware = undefined;
      this.selectedDeviceTypes = [];
      this.form.version = undefined;
      this.form.device_id = undefined;
      this.form.device_operation_office_companies = undefined;
      this.vehicle = undefined;
      this.device = undefined;
      this.form.activationDateRange = [
        defaultStartOfRange,
        currentDate
      ];
    },
    toggleSidebar(): void {
      this.formHidden = !this.formHidden;
    },
  },
  components: {
    FirmwareVersionAutoComplete,
    MultipleDeviceTypeAutoComplete,
    MultipleOperationOfficeAutoComplete,
    DeviceIdAutoComplete,
    VehicleAutoComplete,
    ValidationErrors,
    RangeDatePicker,
  },
});
</script>
